// ════════════════════════════════════════════════════════════════════════
//  Anmeldung
//
//  Nur Erwachsene haben Konten. Magic Link statt Passwort: Lehrkräfte melden
//  sich selten an, und ein Passwort, das man dreimal im Jahr braucht, landet
//  auf einem Zettel am Monitor.
// ════════════════════════════════════════════════════════════════════════

import type { Session, User } from "@supabase/supabase-js";
import type { OttoClient } from "../client/client.js";

export type Role = "teacher" | "school_admin";

export type Identity = {
  readonly user: User;
  readonly profile: {
    readonly schoolId: string;
    readonly role: Role;
    readonly name: string;
  } | null;
  /** Plattformrecht. Unabhängig vom Profil – ein Superadmin gehört zu keiner Schule. */
  readonly isEditorial: boolean;
};

export async function sendMagicLink(
  client: OttoClient,
  email: string,
  redirectTo: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await client.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: {
      emailRedirectTo: redirectTo,
      // Kein Selbstanlegen von Konten: Lehrkräfte werden von der Schule
      // eingeladen, sonst könnte sich jeder ein Konto zu einer Schule bauen.
      shouldCreateUser: false,
    },
  });
  return error ? { ok: false, error: error.message } : { ok: true };
}

export async function signOut(client: OttoClient): Promise<void> {
  await client.auth.signOut();
}

/**
 * Lädt Profil und Plattformrecht zu einer Session.
 *
 * Beides kann fehlen, und das ist kein Fehler: ein Superadmin hat kein Profil,
 * eine gerade eingeladene Lehrkraft noch keins. Die App muss beide Fälle
 * unterscheiden können, statt einen leeren Zustand als Ausfall zu deuten.
 *
 * Ein *Fehler* ist aber auch kein „nein“. Genau das war hier falsch: ein
 * fehlgeschlagener Aufruf wurde wie „hat kein Plattformrecht“ behandelt und
 * führte auf ein Bild ohne Wiederkehr – kein Hinweis, kein zweiter Versuch.
 * Gefunden hat es der neue E2E-Test: unmittelbar nach einem Neustart der
 * Container antwortet PostgREST einige Male mit
 * `PGRST303 "JWT issued at future"`, weil GoTrue und Postgres um Sekunden
 * auseinanderliegen. Draußen genügt eine Sekunde Uhrendrift oder ein wackliges
 * Schulnetz für denselben Effekt.
 *
 * Deshalb: bei einem Fehler kurz erneut versuchen, statt ihn als Antwort zu
 * nehmen. Bleibt es dabei, wirft die Funktion – dann ist es ein Ausfall und
 * kein Rechteproblem, und der Aufrufer darf es als solchen behandeln.
 */
async function withRetry<T>(
  call: () => PromiseLike<{ data: T; error: unknown }>,
  attempts = 3,
): Promise<{ data: T; error: unknown }> {
  let last = await call();
  for (let n = 1; n < attempts && last.error; n++) {
    await new Promise((resolve) => setTimeout(resolve, 250 * n));
    last = await call();
  }
  return last;
}

export async function loadIdentity(
  client: OttoClient,
  session: Session,
): Promise<Identity> {
  const [profile, platformAdmin] = await Promise.all([
    withRetry(() =>
      client
        .from("profiles")
        .select("school_id, role, display_name")
        .eq("id", session.user.id)
        .maybeSingle(),
    ),
    withRetry(() => client.rpc("is_platform_admin")),
  ]);

  // Das Plattformrecht entscheidet über den Zugang zur ganzen App. Ist es
  // nicht zu ermitteln, ist „false“ die falsche Antwort.
  if (platformAdmin.error) {
    throw new Error(
      `Plattformrecht nicht ermittelbar: ${
        platformAdmin.error instanceof Error
          ? platformAdmin.error.message
          : String(platformAdmin.error)
      }`,
    );
  }

  return {
    user: session.user,
    profile: profile.data
      ? {
          schoolId: profile.data.school_id,
          role: profile.data.role as Role,
          name: profile.data.display_name,
        }
      : null,
    isEditorial: platformAdmin.data === true,
  };
}
